/**
 * Hedera Mirror Node API Service
 *
 * Provides access to Hedera account data via the public Mirror Node REST API.
 * No API key required - completely public.
 *
 * Documentation: https://docs.hedera.com/hedera/sdks-and-apis/rest-api
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  HederaAccount,
  HederaTransaction,
  HederaStakingReward,
  HederaToken,
  HederaNFT,
  HederaNetworkStake,
  HederaWalletData,
  HederaTokenWithInfo,
  HederaTokenBalance
} from '../types/hedera.types';

// Hedera Mirror Node base URL (mainnet)
const MIRROR_NODE_URL = 'https://mainnet-public.mirrornode.hedera.com';

// Tinybars to HBAR conversion (1 HBAR = 100,000,000 tinybars)
const TINYBAR_TO_HBAR = 100_000_000;

// Rate limiting - max 50-100 RPS, we'll be conservative
const REQUEST_DELAY_MS = 100;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Max retries for failed requests
const MAX_RETRIES = 2;

// Cache for token info to avoid repeated lookups
const tokenInfoCache = new Map<string, HederaToken>();

// Cache for token prices
const tokenPriceCache = new Map<string, { price: number; timestamp: number }>();
const PRICE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Mapping of known Hedera token IDs to CoinGecko IDs
// This mapping is for popular HTS tokens that are listed on CoinGecko
const HEDERA_TOKEN_COINGECKO_MAP: Record<string, string> = {
  // SaucerSwap (SAUCE)
  '0.0.731861': 'saucerswap',
  // HBARX (Stader liquid staking)
  '0.0.834116': 'hbarx',
  // HeliSwap (HELI)
  '0.0.1083653': 'heliswap',
  // Pangolin Hedera (PNG)
  '0.0.1127305': 'pangolin-hedera',
  // HST (HeadStarter)
  '0.0.456858': 'headstarter',
  // PACK (HashPack)
  '0.0.786931': 'hashpack',
  // JAM (Calaxy)
  '0.0.127877': 'calaxy',
  // KARATE
  '0.0.4989860': 'karate-combat',
  // GRELF
  '0.0.3155971': 'grelf',
  // Dovu (DOV)
  '0.0.457685': 'dovu-2',
};

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with timeout and retry
 */
const fetchWithTimeout = async (url: string, retries = MAX_RETRIES): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);

    // Retry on network errors
    if (retries > 0 && (error.name === 'AbortError' || error.name === 'TypeError')) {
      logger.warn('HederaAPI', `Request failed, retrying... (${retries} left)`);
      await delay(1000); // Wait 1s before retry
      return fetchWithTimeout(url, retries - 1);
    }

    throw error;
  }
};

/**
 * Convert tinybars to HBAR
 */
export const tinybarToHbar = (tinybars: number): number => {
  return tinybars / TINYBAR_TO_HBAR;
};

/**
 * Validate Hedera account ID format (0.0.xxxxx)
 */
export const isValidAccountId = (accountId: string): boolean => {
  const pattern = /^0\.0\.\d+$/;
  return pattern.test(accountId);
};

/**
 * Format account ID for display
 */
export const formatAccountId = (accountId: string): string => {
  return accountId; // Already in readable format
};

/**
 * Hedera API Service
 */
export const hederaAPI = {
  /**
   * Get account information
   */
  async getAccount(accountId: string): Promise<HederaAccount | null> {
    try {
      if (!isValidAccountId(accountId)) {
        logger.warn('HederaAPI', `Invalid account ID format: ${accountId}`);
        return null;
      }

      const response = await fetchWithTimeout(`${MIRROR_NODE_URL}/api/v1/accounts/${accountId}`);

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('HederaAPI', `Account not found: ${accountId}`);
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data as HederaAccount;
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch account ${accountId}`, error);
      throw error;
    }
  },

  /**
   * Get account transactions
   */
  async getTransactions(
    accountId: string,
    limit: number = 50,
    order: 'asc' | 'desc' = 'desc'
  ): Promise<HederaTransaction[]> {
    try {
      const response = await fetch(
        `${MIRROR_NODE_URL}/api/v1/transactions?account.id=${accountId}&limit=${limit}&order=${order}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.transactions || [];
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch transactions for ${accountId}`, error);
      return [];
    }
  },

  /**
   * Get staking rewards history
   */
  async getStakingRewards(
    accountId: string,
    limit: number = 100
  ): Promise<HederaStakingReward[]> {
    try {
      const response = await fetch(
        `${MIRROR_NODE_URL}/api/v1/accounts/${accountId}/rewards?limit=${limit}&order=desc`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return []; // No rewards yet
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.rewards || [];
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch rewards for ${accountId}`, error);
      return [];
    }
  },

  /**
   * Get token balances for an account
   */
  async getTokenBalances(accountId: string): Promise<HederaTokenBalance[]> {
    try {
      const response = await fetch(
        `${MIRROR_NODE_URL}/api/v1/accounts/${accountId}/tokens?limit=100`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.tokens || [];
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch tokens for ${accountId}`, error);
      return [];
    }
  },

  /**
   * Get token information
   */
  async getTokenInfo(tokenId: string): Promise<HederaToken | null> {
    // Check cache first
    if (tokenInfoCache.has(tokenId)) {
      return tokenInfoCache.get(tokenId)!;
    }

    try {
      const response = await fetch(`${MIRROR_NODE_URL}/api/v1/tokens/${tokenId}`);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const tokenInfo = data as HederaToken;

      // Cache the result
      tokenInfoCache.set(tokenId, tokenInfo);

      return tokenInfo;
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch token info for ${tokenId}`, error);
      return null;
    }
  },

  /**
   * Get NFTs owned by an account
   */
  async getNFTs(accountId: string, limit: number = 100): Promise<HederaNFT[]> {
    try {
      const response = await fetch(
        `${MIRROR_NODE_URL}/api/v1/accounts/${accountId}/nfts?limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.nfts || [];
    } catch (error) {
      logger.error('HederaAPI', `Failed to fetch NFTs for ${accountId}`, error);
      return [];
    }
  },

  /**
   * Get network staking information
   */
  async getNetworkStake(): Promise<HederaNetworkStake | null> {
    try {
      const response = await fetch(`${MIRROR_NODE_URL}/api/v1/network/stake`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('HederaAPI', 'Failed to fetch network stake info', error);
      return null;
    }
  },

  /**
   * Get HBAR price from CoinGecko
   */
  async getHbarPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'hedera-hashgraph',
        vs_currencies: 'usd'
      });
      return data['hedera-hashgraph']?.usd || 0;
    } catch (error) {
      logger.error('HederaAPI', 'Failed to fetch HBAR price', error);
      return 0;
    }
  },

  /**
   * Get token prices from CoinGecko for known Hedera tokens
   * Returns a map of tokenId -> priceUsd
   */
  async getTokenPrices(tokenIds: string[]): Promise<Record<string, number>> {
    const prices: Record<string, number> = {};
    const now = Date.now();

    // Collect CoinGecko IDs for tokens we know
    const coingeckoIds: string[] = [];
    const tokenIdToCgId: Record<string, string> = {};

    for (const tokenId of tokenIds) {
      // Check cache first
      const cached = tokenPriceCache.get(tokenId);
      if (cached && (now - cached.timestamp) < PRICE_CACHE_DURATION) {
        prices[tokenId] = cached.price;
        continue;
      }

      // Check if we have a CoinGecko mapping
      const cgId = HEDERA_TOKEN_COINGECKO_MAP[tokenId];
      if (cgId) {
        coingeckoIds.push(cgId);
        tokenIdToCgId[tokenId] = cgId;
      }
    }

    // Fetch prices from CoinGecko if we have any
    if (coingeckoIds.length > 0) {
      try {
        const data = await callCoinGeckoAPI('/simple/price', {
          ids: coingeckoIds.join(','),
          vs_currencies: 'usd'
        });

        // Map back to token IDs
        for (const [tokenId, cgId] of Object.entries(tokenIdToCgId)) {
          const price = data[cgId]?.usd || 0;
          prices[tokenId] = price;
          tokenPriceCache.set(tokenId, { price, timestamp: now });
        }
      } catch (error) {
        logger.error('HederaAPI', 'Failed to fetch token prices', error);
      }
    }

    return prices;
  },

  /**
   * Check if a token has a known price mapping
   */
  hasKnownPrice(tokenId: string): boolean {
    return tokenId in HEDERA_TOKEN_COINGECKO_MAP;
  },

  /**
   * Get complete wallet data for an account
   */
  async getWalletData(accountId: string, hbarPrice: number): Promise<HederaWalletData | null> {
    try {
      // Fetch account info
      const account = await this.getAccount(accountId);
      if (!account) {
        return null;
      }

      await delay(REQUEST_DELAY_MS);

      // Fetch transactions
      const transactions = await this.getTransactions(accountId, 25);
      await delay(REQUEST_DELAY_MS);

      // Fetch staking rewards
      const rewards = await this.getStakingRewards(accountId);
      await delay(REQUEST_DELAY_MS);

      // Fetch token balances
      const tokenBalances = await this.getTokenBalances(accountId);
      await delay(REQUEST_DELAY_MS);

      // Fetch token info for each token (with rate limiting)
      const tokensWithInfo: HederaTokenWithInfo[] = [];
      for (const tb of tokenBalances.slice(0, 20)) { // Limit to 20 tokens
        const tokenInfo = await this.getTokenInfo(tb.token_id);
        if (tokenInfo) {
          tokensWithInfo.push({
            tokenId: tb.token_id,
            symbol: tokenInfo.symbol,
            name: tokenInfo.name,
            balance: tb.balance / Math.pow(10, tokenInfo.decimals),
            decimals: tokenInfo.decimals,
            type: tokenInfo.type
          });
        }
        await delay(REQUEST_DELAY_MS);
      }

      // Fetch NFTs
      const nfts = await this.getNFTs(accountId, 50);

      // Calculate totals
      const balanceHbar = tinybarToHbar(account.balance.balance);
      const pendingRewardHbar = tinybarToHbar(account.pending_reward);
      const totalRewardsHbar = rewards.reduce(
        (sum, r) => sum + tinybarToHbar(r.amount),
        0
      );

      return {
        accountId,
        balance: balanceHbar,
        balanceUsd: balanceHbar * hbarPrice,
        pendingReward: pendingRewardHbar,
        pendingRewardUsd: pendingRewardHbar * hbarPrice,
        stakedNodeId: account.staked_node_id,
        stakedAccountId: account.staked_account_id,
        tokens: tokensWithInfo,
        nfts,
        recentTransactions: transactions,
        totalRewardsReceived: totalRewardsHbar,
        lastUpdated: Date.now()
      };
    } catch (error) {
      logger.error('HederaAPI', `Failed to get wallet data for ${accountId}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple accounts
   */
  async getMultipleWalletsData(
    accountIds: string[],
    hbarPrice: number
  ): Promise<HederaWalletData[]> {
    const results: HederaWalletData[] = [];

    for (const accountId of accountIds) {
      const walletData = await this.getWalletData(accountId, hbarPrice);
      if (walletData) {
        results.push(walletData);
      }
      // Add delay between accounts to respect rate limits
      await delay(500);
    }

    return results;
  },

  /**
   * Calculate transaction summary (in/out)
   */
  calculateTransactionSummary(
    transactions: HederaTransaction[],
    accountId: string
  ): { totalIn: number; totalOut: number; netFlow: number } {
    let totalIn = 0;
    let totalOut = 0;

    for (const tx of transactions) {
      for (const transfer of tx.transfers || []) {
        if (transfer.account === accountId) {
          const amountHbar = tinybarToHbar(transfer.amount);
          if (transfer.amount > 0) {
            totalIn += amountHbar;
          } else {
            totalOut += Math.abs(amountHbar);
          }
        }
      }
    }

    return {
      totalIn,
      totalOut,
      netFlow: totalIn - totalOut
    };
  },

  /**
   * Get transaction type display name
   */
  getTransactionTypeName(type: string): string {
    const typeMap: Record<string, string> = {
      'CRYPTOTRANSFER': 'Transfer',
      'CRYPTOCREATEACCOUNT': 'Account Creation',
      'CRYPTOUPDATEACCOUNT': 'Account Update',
      'CRYPTODELETE': 'Account Deletion',
      'TOKENASSOCIATE': 'Token Association',
      'TOKENTRANSFER': 'Token Transfer',
      'TOKENMINT': 'Token Mint',
      'TOKENBURN': 'Token Burn',
      'CONTRACTCALL': 'Contract Call',
      'CONTRACTCREATE': 'Contract Creation',
      'CONSENSUSSUBMITMESSAGE': 'HCS Message',
      'SCHEDULECREATE': 'Schedule Create',
      'SCHEDULESIGN': 'Schedule Sign',
      'FREEZE': 'Freeze',
      'ETHEREUMTRANSACTION': 'Ethereum TX'
    };

    return typeMap[type] || type;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): Date {
    // Hedera timestamp format: "1234567890.123456789" (seconds.nanoseconds)
    const seconds = parseFloat(timestamp.split('.')[0]);
    return new Date(seconds * 1000);
  }
};

export default hederaAPI;
